type CompatMetricsSnapshotV1 = {
  schemaVersion: 1;
  startedAt: number;
  lastUpdatedAt: number;
  env: string;
  counts: Record<string, number>;
  lastSeen: Record<string, number>;
};

const STORAGE_KEY_V1 = 'compat:metrics:v1';
const SCHEMA_VERSION = 1 as const;

const ENABLED = (() => {
  const raw = String(import.meta.env.VITE_COMPAT_METRICS ?? '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
})();

const lastEmitAtByKey = new Map<string, number>();

function now(): number {
  return Date.now();
}

function safeParseSnapshot(raw: string | null): CompatMetricsSnapshotV1 | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CompatMetricsSnapshotV1>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.schemaVersion !== SCHEMA_VERSION) return null;
    if (typeof parsed.startedAt !== 'number') return null;
    if (typeof parsed.lastUpdatedAt !== 'number') return null;
    if (!parsed.counts || typeof parsed.counts !== 'object') return null;
    if (!parsed.lastSeen || typeof parsed.lastSeen !== 'object') return null;
    return parsed as CompatMetricsSnapshotV1;
  } catch {
    return null;
  }
}

function createEmptySnapshot(): CompatMetricsSnapshotV1 {
  const t = now();
  return {
    schemaVersion: SCHEMA_VERSION,
    startedAt: t,
    lastUpdatedAt: t,
    env: String(import.meta.env.MODE ?? ''),
    counts: {},
    lastSeen: {},
  };
}

function loadSnapshot(): CompatMetricsSnapshotV1 {
  const existing = safeParseSnapshot(typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY_V1) : null);
  return existing ?? createEmptySnapshot();
}

function persistSnapshot(s: CompatMetricsSnapshotV1): void {
  try {
    localStorage.setItem(STORAGE_KEY_V1, JSON.stringify(s));
  } catch {
    // ignore storage write failures (quota/private mode)
  }
}

/**
 * Increment a local-only compat metric counter (guarded by VITE_COMPAT_METRICS).
 * Dedupe by `dedupeKey` within `dedupeMs` to avoid spam on re-render/hot paths.
 */
export function incrementCompatMetric(
  eventName: string,
  options?: {
    dedupeKey?: string;
    dedupeMs?: number;
  }
): void {
  if (!ENABLED) return;
  if (typeof window === 'undefined') return;
  const name = String(eventName || '').trim();
  if (!name) return;

  const dedupeKey = String(options?.dedupeKey ?? name);
  const dedupeMs = typeof options?.dedupeMs === 'number' ? Math.max(0, options!.dedupeMs) : 10_000;
  const t = now();
  const last = lastEmitAtByKey.get(dedupeKey) ?? 0;
  if (dedupeMs > 0 && t - last < dedupeMs) return;
  lastEmitAtByKey.set(dedupeKey, t);

  const snap = loadSnapshot();
  snap.counts[name] = (snap.counts[name] ?? 0) + 1;
  snap.lastSeen[name] = t;
  snap.lastUpdatedAt = t;
  persistSnapshot(snap);
}

export function getCompatMetricsSnapshot(): CompatMetricsSnapshotV1 | null {
  if (!ENABLED) return null;
  if (typeof window === 'undefined') return null;
  return loadSnapshot();
}

export function isCompatMetricsEnabled(): boolean {
  return ENABLED;
}

